<script setup lang="ts">
import winePlaceholder from '@/assets/images/wine_placeholder.png'
import RatingStars from '@/components/RatingStars.vue'
import { ScannedWine } from '@/types'
import { formatDateRu } from '@/utils/dates'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

// Props
interface Props {
    scannedWine: ScannedWine
    showActions?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    showActions: false,
})

// Emits
const emit = defineEmits<{
    click: [scannedWine: ScannedWine]
}>()

const router = useRouter()

const wine = computed(() => props.scannedWine.wine)

const ratingValue = computed(() => {
    // Приоритет: оценка пользователя > средняя оценка
    return (props.scannedWine.review?.rating ||
        props.scannedWine.wine.average_rating ||
        0) as number
})

const displayedComment = computed(() => {
    // Показываем комментарий пользователя, если есть
    if (props.scannedWine.review?.comment) {
        return props.scannedWine.review.comment
    }

    // Иначе показываем описание вина или заглушку
    return (
        props.scannedWine.wine.description ||
        'Опишите ваши вкусовые ощущения, сладость, терпкость или чем хотите поделиться'
    )
})

const imageSrc = computed(() => {
    const url = props.scannedWine.wine.bottle_image?.url
    if (!url) return winePlaceholder
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:'))
        return url
    return winePlaceholder
})

const formatDate = (dateString: string) => formatDateRu(dateString)

// Methods
const handleClick = () => {
    emit('click', props.scannedWine)
    // Передаем ID скана, а не ID вина
    router.push({ name: 'wine-info', params: { id: props.scannedWine.id } })
}
</script>

<template>
    <article
        class="scanned-wine-card"
        :class="{ 'scanned-wine-card_no-rating': ratingValue === 0 }"
        v-if="scannedWine"
        @click="handleClick"
    >
        <div class="scanned-wine-card__content">
            <div class="scanned-wine-card__rating">
                <RatingStars :rating="ratingValue" size="medium" />
            </div>
            <div class="scanned-wine-card__wrap">
                <div class="scanned-wine-card__date">
                    {{ formatDate(scannedWine.created_at) }}
                </div>
                <h3 class="scanned-wine-card__title">{{ wine.name }}, {{ wine.year }}</h3>
            </div>
            <p
                class="scanned-wine-card__desc"
                :class="{ 'scanned-wine-card__desc_user-review': !!scannedWine.review?.comment }"
            >
                {{ displayedComment }}
            </p>
            <div class="scanned-wine-card__cta" @click.stop="handleClick">Подробнее →</div>
        </div>

        <div class="scanned-wine-card__image">
            <img :src="imageSrc" :alt="wine.name" />
        </div>
    </article>
</template>

<style scoped lang="stylus">
.scanned-wine-card
    display flex
    align-items stretch
    justify-content space-between
    gap 8px
    padding 0 0 24px
    width 100%
    height 244px
    border-bottom 1px solid var(--app-border-light)
    cursor pointer

    &__content
        display flex
        flex-direction column
        gap 12px

    &__image
        position relative
        min-width 81px
        height 100%
        flex 0 0 81px
        overflow hidden
        img
            width 100%
            height 100%
            object-fit contain

    &__date
        font-size 10px
        line-height 12px
        font-weight 400
        color var(--app-hint-color)
        margin-bottom 4px

    &__title
        font-size 16px
        line-height 21px
        font-weight 600
        color var(--app-text-color)
        text-transform uppercase

    &__winemaker
        font-size 12px
        line-height 16px
        font-weight 400
        color var(--app-hint-color)

    &__desc
        font-size 14px
        line-height 21px
        font-weight 400
        color var(--app-text-disabled)
        overflow hidden
        display -webkit-box
        -webkit-line-clamp 4
        -webkit-box-orient vertical
        word-break break-word
        ^[0]_no-rating &
            color var(--app-text-disabled)
        &_user-review
            color var(--app-text-color)

    &__cta
        margin-top auto
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-main-accent-color)
</style>
